// src/CurrencyContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { getSupportedCurrencies, updateUserCurrency, getUserCurrency } from './currencyApi';
import { useTranslation } from 'react-i18next';

const CurrencyContext = createContext();

// ✅ ALLOWED CURRENCIES - Only these will be shown to users
const ALLOWED_CURRENCIES = ['YER', 'YRN', 'USD', 'SAR'];

// ✅ Fallback currencies (filtered to allowed only)
const FALLBACK_CURRENCIES = [
  { code: 'YER', symbol: 'ر.ي', name: 'ريال يمني قديم', decimals: 0 },
  { code: 'YRN', symbol: 'ر.ي.ج', name: 'ريال يمني جديد', decimals: 0 },
  { code: 'USD', symbol: '$', name: 'دولار أمريكي', decimals: 2 },
  { code: 'SAR', symbol: 'ر.س', name: 'ريال سعودي', decimals: 2 },
];

export function useCurrency() {
  return useContext(CurrencyContext);
}

export function CurrencyProvider({ children }) {
  const { i18n } = useTranslation();
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState('YER');
  const [loading, setLoading] = useState(true);

  const getUserId = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      return user?.Id || user?.id || null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const loadCurrencies = async () => {
      setLoading(true);
      try {
        // 1. Fetch all supported currencies
        const data = await getSupportedCurrencies();
        
        // ✅ Filter to only allowed currencies
        const allCurrencies = data.Currencies || FALLBACK_CURRENCIES;
        const filteredCurrencies = allCurrencies.filter(currency => 
          ALLOWED_CURRENCIES.includes(currency.code)
        );
        
        setCurrencies(filteredCurrencies);

        // 2. Load user's preferred currency
        const userId = getUserId();
        if (userId) {
          try {
            const userCurrency = await getUserCurrency(userId);
            // ✅ Validate currency is allowed
            if (ALLOWED_CURRENCIES.includes(userCurrency)) {
              setSelectedCurrency(userCurrency);
              localStorage.setItem('selectedCurrency', userCurrency);
            } else {
              // Default to YER if saved currency is not allowed
              setSelectedCurrency('YER');
              localStorage.setItem('selectedCurrency', 'YER');
            }
          } catch (error) {
            console.log('Using default currency');
            const storedCurrency = localStorage.getItem('selectedCurrency') || 'YER';
            // ✅ Validate stored currency
            const validCurrency = ALLOWED_CURRENCIES.includes(storedCurrency) ? storedCurrency : 'YER';
            setSelectedCurrency(validCurrency);
          }
        } else {
          // Guest user: load from localStorage
          const storedCurrency = localStorage.getItem('selectedCurrency') || 'YER';
          // ✅ Validate stored currency
          const validCurrency = ALLOWED_CURRENCIES.includes(storedCurrency) ? storedCurrency : 'YER';
          setSelectedCurrency(validCurrency);
        }
      } catch (error) {
        console.error('Error loading currencies:', error);
        // ✅ Fallback to allowed currencies only
        setCurrencies(FALLBACK_CURRENCIES);
        const storedCurrency = localStorage.getItem('selectedCurrency') || 'YER';
        const validCurrency = ALLOWED_CURRENCIES.includes(storedCurrency) ? storedCurrency : 'YER';
        setSelectedCurrency(validCurrency);
      } finally {
        setLoading(false);
      }
    };

    loadCurrencies();

    const handleUserChange = () => {
      loadCurrencies();
    };
    window.addEventListener('userChanged', handleUserChange);
    return () => {
      window.removeEventListener('userChanged', handleUserChange);
    };
  }, []);

  const changeCurrency = async (currencyCode) => {
    try {
      // ✅ Validate currency before changing
      if (!ALLOWED_CURRENCIES.includes(currencyCode)) {
        console.error(`Currency ${currencyCode} is not allowed`);
        return false;
      }

      // 1. Update state immediately
      setSelectedCurrency(currencyCode);
      localStorage.setItem('selectedCurrency', currencyCode);

      // 2. Update backend if user is logged in
      const userId = getUserId();
      if (userId) {
        await updateUserCurrency(userId, currencyCode);
        console.log(`✅ Currency updated to ${currencyCode} in database`);
      } else {
        console.log(`💾 Currency ${currencyCode} saved to localStorage (guest mode)`);
      }

      // 3. Dispatch event so other components can react
      window.dispatchEvent(new CustomEvent('currencyChanged', { detail: { currency: currencyCode } }));
      
      return true;
    } catch (error) {
      console.error('Error changing currency:', error);
      return false;
    }
  };

  const getCurrencyInfo = (code) => {
    return currencies.find(c => c.code === code) || currencies[0] || FALLBACK_CURRENCIES[0];
  };

  const getCurrencySymbol = (code = selectedCurrency) => {
    const info = getCurrencyInfo(code);
    return info.symbol;
  };

  const formatPrice = (amount, code = selectedCurrency) => {
    const info = getCurrencyInfo(code);
    const formattedAmount = parseFloat(amount).toFixed(info.decimals);
    return `${info.symbol}${formattedAmount}`;
  };

  const value = {
    currencies, // ✅ Now only contains YER, USD, SAR
    selectedCurrency,
    changeCurrency,
    getCurrencyInfo,
    getCurrencySymbol,
    formatPrice,
    loading
  };

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}
